import React from "react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/lib/config";
import { ArrowRight, Hammer, Shield, Sparkles, Ruler } from "lucide-react";

const FEATURES = [
  { icon: Hammer, title: "Craftsmanship", body: "Precision cuts, level substrates, tight grout lines." },
  { icon: Shield, title: "Reliability", body: "Insured, on-time, and on-budget from measure to finish." },
  { icon: Sparkles, title: "Quality Materials", body: "Hardwood, porcelain, natural stone, luxury vinyl." },
  { icon: Ruler, title: "Custom Design", body: "Mosaics, chevrons, herringbone — designed with you." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden bg-[color:var(--brand-ink)]">
        <div className="absolute inset-0">
          <img
            src={IMAGES.hero}
            alt="Custom tile kitchen backsplash"
            loading="eager"
            fetchPriority="high"
            className="w-full h-full object-cover opacity-80"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-black/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-32 lg:pt-32 lg:pb-44 text-[color:var(--brand-cream)]">
          <div className="max-w-2xl fade-up">
            <div className="eyebrow text-white/80">Flooring &amp; Tile · Florida</div>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-white drop-shadow-[0_2px_20px_rgba(0,0,0,0.5)]">
              JFeva Solutions, <span className="italic text-white/95">LLC</span>
            </h1>
            <p className="mt-6 font-display italic text-2xl sm:text-3xl text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.5)]">
              &ldquo;Our quality will floor you.&rdquo;
            </p>
            <p className="mt-6 max-w-lg text-white/90 text-base leading-relaxed">
              Hardwood, laminate, luxury vinyl, and custom tile — installed by craftsmen who care about
              every seam, every cut, every finish.
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <Link
                data-testid="hero-quote-btn"
                to="/quote"
                className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-cream)] text-[color:var(--brand-ink)] px-6 py-3 text-sm font-medium hover:-translate-y-0.5 hover:shadow-xl transition-[transform,box-shadow]"
              >
                Request a Quote <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                data-testid="hero-book-btn"
                to="/booking"
                className="inline-flex items-center gap-2 rounded-full border border-white/40 text-white px-6 py-3 text-sm font-medium hover:bg-white/10 transition-colors"
              >
                Book Installation
              </Link>
              <Link
                data-testid="hero-gallery-btn"
                to="/gallery"
                className="inline-flex items-center gap-2 rounded-full text-white/80 px-4 py-3 text-sm hover:text-white transition-colors"
              >
                View Gallery <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
          <div className="md:col-span-7">
            <div className="eyebrow text-[color:var(--brand-wood)]">Why choose Jfeva</div>
            <h2 className="mt-3 font-display text-4xl sm:text-5xl tracking-tight leading-tight">
              A floor is the surface everyone touches.<br/>
              <span className="text-[color:var(--brand-woodDark)] italic">We treat it like art.</span>
            </h2>
          </div>
          <p className="md:col-span-5 text-[color:var(--brand-woodDark)] text-base leading-relaxed">
            From backsplashes to whole-home floors, we bring the same care to every project — big or small.
            Serving Florida and surrounding areas with clean workmanship and honest pricing.
          </p>
        </div>

        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {FEATURES.map(({ icon: Icon, title, body }) => (
            <div
              key={title}
              data-testid={`feature-${title.toLowerCase().replace(/\s+/g, "-")}`}
              className="rounded-2xl border border-black/5 bg-white p-8 hover-lift"
            >
              <div className="h-11 w-11 rounded-full bg-[color:var(--brand-stone)] text-[color:var(--brand-woodDark)] flex items-center justify-center">
                <Icon className="h-5 w-5" />
              </div>
              <h3 className="mt-6 font-display text-2xl">{title}</h3>
              <p className="mt-2 text-sm text-[color:var(--brand-woodDark)] leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BENTO SHOWCASE */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-8 relative rounded-2xl overflow-hidden aspect-[16/10]">
            <img src={IMAGES.kitchen} alt="Kitchen tile" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
            <div className="absolute bottom-6 left-6 text-white">
              <div className="eyebrow opacity-80">Kitchens</div>
              <div className="font-display text-2xl">Backsplashes &amp; floors</div>
            </div>
          </div>
          <div className="md:col-span-4 relative rounded-2xl overflow-hidden aspect-[4/5]">
            <img src={IMAGES.bathroom} alt="Bathroom tile" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
            <div className="absolute bottom-6 left-6 text-white">
              <div className="eyebrow opacity-80">Bathrooms</div>
              <div className="font-display text-2xl">Showers &amp; floors</div>
            </div>
          </div>
          <div className="md:col-span-5 relative rounded-2xl overflow-hidden aspect-[4/3]">
            <img src={IMAGES.living} alt="Living space wood floors" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
            <div className="absolute bottom-6 left-6 text-white">
              <div className="eyebrow opacity-80">Living Spaces</div>
              <div className="font-display text-2xl">Hardwood &amp; vinyl</div>
            </div>
          </div>
          <div className="md:col-span-7 relative rounded-2xl overflow-hidden aspect-[16/9]">
            <img src={IMAGES.custom} alt="Custom tile" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
            <div className="absolute bottom-6 left-6 text-white">
              <div className="eyebrow opacity-80">Custom Work</div>
              <div className="font-display text-2xl">Mosaics · herringbone · chevron</div>
            </div>
          </div>
        </div>

        <div className="mt-12 flex justify-center">
          <Link
            data-testid="home-view-gallery-btn"
            to="/gallery"
            className="inline-flex items-center gap-2 rounded-full border border-[color:var(--brand-ink)]/20 px-6 py-3 text-sm font-medium hover:bg-[color:var(--brand-ink)] hover:text-[color:var(--brand-cream)] transition-colors"
          >
            Explore Full Gallery <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* CTA STRIP */}
      <section className="relative max-w-7xl mx-auto px-6 lg:px-10 pb-24">
        <div className="rounded-3xl bg-[color:var(--brand-ink)] text-[color:var(--brand-cream)] p-10 lg:p-16 grid md:grid-cols-12 items-center gap-8">
          <div className="md:col-span-8">
            <div className="eyebrow text-white/60">Ready to start?</div>
            <h3 className="mt-3 font-display text-3xl sm:text-4xl leading-tight">
              Get a personal quote for your floor or tile project.
            </h3>
            <p className="mt-3 text-white/70 text-sm">
              Send us photos and details — we respond within one business day.
            </p>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              data-testid="cta-strip-quote-btn"
              to="/quote"
              className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-cream)] text-[color:var(--brand-ink)] px-6 py-3 text-sm font-medium hover:-translate-y-0.5 transition-transform"
            >
              Request Quote <ArrowRight className="h-4 w-4"/>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
